import api from './client';
import type { Category, Blahblah, PaginatedResponse, FeedSortType, ThreadFollow, FollowedThread } from '@/types';
import { transformCategory, transformBlahblah, transformPaginatedResponse, transformThreadFollow, transformFollowedThread } from '@/lib/utils';

// ==================== CATEGORIES ====================

export async function getCategories(): Promise<Category[]> {
  const { data } = await api.get('/categories');
  return (data.categories || []).map(transformCategory).filter(Boolean);
}

export async function getStarredCategories(): Promise<Category[]> {
  const { data } = await api.get('/categories/starred');
  return (data.categories || []).map(transformCategory).filter(Boolean);
}

export async function starCategory(categoryId: number): Promise<void> {
  await api.post(`/categories/${categoryId}/star`);
}

export async function unstarCategory(categoryId: number): Promise<void> {
  await api.delete(`/categories/${categoryId}/star`);
}

export async function getCategoryFeed(
  slug: string,
  cursor?: string,
  limit = 20,
  sort: FeedSortType = 'latest'
): Promise<PaginatedResponse<Blahblah>> {
  const params = new URLSearchParams();
  if (cursor) params.set('cursor', cursor);
  params.set('limit', limit.toString());
  params.set('sort', sort);

  const { data } = await api.get(`/categories/${slug}/feed?${params.toString()}`);
  return transformPaginatedResponse(data, transformBlahblah);
}

export async function updateBlahblahCategories(
  blahblahId: string,
  categoryIds: number[]
): Promise<void> {
  await api.post(`/blahblahs/${blahblahId}/categories`, { category_ids: categoryIds });
}

// ==================== THREAD FOLLOWING ====================

export async function followThread(
  blahblahId: string,
  notificationsEnabled = true
): Promise<ThreadFollow> {
  const { data } = await api.post(`/blahblahs/${blahblahId}/follow`, {
    notifications_enabled: notificationsEnabled,
  });
  return transformThreadFollow(data) as ThreadFollow;
}

export async function unfollowThread(blahblahId: string): Promise<void> {
  await api.delete(`/blahblahs/${blahblahId}/follow`);
}

export async function updateThreadFollow(
  blahblahId: string,
  notificationsEnabled: boolean
): Promise<ThreadFollow> {
  const { data } = await api.patch(`/blahblahs/${blahblahId}/follow`, {
    notifications_enabled: notificationsEnabled,
  });
  return transformThreadFollow(data) as ThreadFollow;
}

export async function getFollowedThreads(
  cursor?: string,
  limit = 20
): Promise<PaginatedResponse<FollowedThread>> {
  const params = new URLSearchParams();
  if (cursor) params.set('cursor', cursor);
  params.set('limit', limit.toString());

  const { data } = await api.get(`/users/me/followed-threads?${params.toString()}`);
  return transformPaginatedResponse(data, transformFollowedThread);
}

// ==================== AGE VERIFICATION ====================

export async function verifyAge(
  birthDate: string,
  confirmed: boolean
): Promise<{ isAdultVerified: boolean; adultVerifiedAt: string | null }> {
  const { data } = await api.post('/users/me/verify-age', {
    birth_date: birthDate,
    confirmed,
  });
  return {
    isAdultVerified: data.is_adult_verified ?? data.isAdultVerified,
    adultVerifiedAt: data.adult_verified_at || data.adultVerifiedAt || null,
  };
}
